import React, { useState } from "react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { useAuth } from "../../AuthContext";

export default function AccountSettings() {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const sendVerification = async () => {
    await verifyBeforeUpdateEmail(user, newEmail);
    setStatus(
      `Check the inbox for ${newEmail} — we sent a confirmation link there. Your login email won't change until you click it.`
    );
    setNewEmail("");
    setPassword("");
    setNeedsPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");

    if (!newEmail || newEmail === user.email) {
      setError("Enter a different email address.");
      return;
    }

    setLoading(true);
    try {
      if (needsPassword) {
        if (!password) {
          setError("Enter your current password to confirm this change.");
          setLoading(false);
          return;
        }
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }
      await sendVerification();
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        setNeedsPassword(true);
        setError("For your security, enter your current password to confirm this change.");
      } else if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("That password doesn't match your account. Try again.");
      } else if (err.code === "auth/email-already-in-use") {
        setError("Another account is already using that email.");
      } else if (err.code === "auth/invalid-email") {
        setError("That doesn't look like a valid email address.");
      } else {
        console.error("Email update error:", err);
        setError("Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: "rgba(255,255,255,0.05)",
    padding: "20px",
    borderRadius: "10px",
    maxWidth: "480px",
  };

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>Account</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Manage the email address you use to log in.
      </p>

      <div style={cardStyle}>
        <label style={{ display: "block", marginBottom: "8px" }}>Current email</label>
        <p style={{ marginBottom: "20px" }}>{user?.email}</p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: "block", marginBottom: "8px" }}>New email</label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
          />

          {needsPassword && (
            <>
              <label style={{ display: "block", marginBottom: "8px" }}>Current password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
              />
            </>
          )}

          {error && <p style={{ color: "#f87171", marginBottom: "12px" }}>{error}</p>}
          {status && <p style={{ color: "#4ade80", marginBottom: "12px" }}>{status}</p>}

          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? "rgba(255,255,255,0.08)" : "linear-gradient(90deg, #6D28D9, #A855F7)",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: 600,
              cursor: loading ? "default" : "pointer",
              width: "100%",
            }}
          >
            {loading ? "Sending..." : "Update Email"}
          </button>
        </form>
      </div>
    </div>
  );
}
